import { useState } from "react";
import { Alert, AlertTitle, Box, Link as MuiLink, Stack, Typography } from "@mui/material";
import { ReferenceField, TextField } from "react-admin";
import { Link as RouterLink } from "react-router-dom";
import { ResourceName } from "@/types/common";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import {
  DIFFERING_FIELD_LABELS,
  DETECTION_REASON_LABELS,
  type DuplicateClaimantPair,
} from "@/types/duplicateClaimantPair";
import { formatAmount } from "@/resources/duplicate-claimant-pairs/components/DuplicateClaimantPairListCells";
import { DuplicateClaimantPairReviewDialog } from "@/resources/duplicate-claimant-pairs/components/DuplicateClaimantPairReviewDialog";
import { counterpartOf, useClaimantDuplicatePairs } from "../hooks/useClaimantDuplicatePairs";

/**
 * Warns the analyst that this claimant may be the same person as another record
 * (Story #6718, detection from #6518).
 *
 * WHY THIS EXISTS SEPARATELY FROM THE REVIEW LIST (#6717). The list is where
 * duplicates get worked in bulk; it is not where the problem gets NOTICED. The
 * person asking "why is this claimant's accumulation lower than I expected" is
 * standing on this page, and until now saw nothing. The Winstead split was
 * traced on this screen by hand.
 *
 * DELIBERATELY READ-ONLY. No dismiss, no merge. Those live in #6717 and #6519
 * respectively, so pair state has exactly one writer per action — two surfaces
 * that could both mutate a pair would eventually disagree about it.
 *
 * Shows on BOTH rows of a pair, not just the larger one. Users often arrive at
 * the smaller row; that is what makes the number look wrong.
 */
interface SuspectedDuplicateBannerProps {
  claimantId?: string;
  /**
   * `quiet`: one muted line per pair with the counterpart's name and a link to the
   * review list (Feature #7659): advisory context, not a blocker, so no Alert.
   */
  variant?: "alert" | "quiet";
}

export const SuspectedDuplicateBanner = ({ claimantId, variant = "alert" }: SuspectedDuplicateBannerProps) => {
  const { canList } = useResourcePermissions(ResourceName.DUPLICATE_CLAIMANT_PAIRS);
  const { pairs, isLoading, error } = useClaimantDuplicatePairs(claimantId);
  // Quiet variant: Review opens the pair's side-by-side dialog here (Dillon,
  // 2026-10-28: don't leave the page). Same dialog as the review list; its
  // refresh() re-reads the record and this hook's list. Keyed so a second pair
  // starts clean, as the list does.
  const [reviewing, setReviewing] = useState<DuplicateClaimantPair | null>(null);

  // Silent while LOADING and when the answer is genuinely "none". A "checking for
  // duplicates" placeholder on every claimant page would be noise on the overwhelming
  // majority of records that have none.
  if (isLoading || !claimantId) return null;

  // NOT silent on failure. The purpose of this banner is to stop someone trusting an
  // accumulation the system suspects is split, so rendering nothing when the check
  // could not run says "no duplicates" — the one thing we must never say on no
  // evidence. #6518 not being deployed, a 403, or a 500 all land here, and a user
  // would otherwise get no warning and no hint the check didn't happen.
  if (error) {
    if (variant === "quiet") {
      return (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: "0.8125rem" }}
          data-testid="suspected-duplicate-quiet"
        >
          Could not check for duplicate records; totals may be split across records.
        </Typography>
      );
    }
    return (
      <Alert severity="info" sx={{ mb: 2, "& .MuiAlert-message": { fontSize: "0.8125rem" } }}>
        Could not check whether this claimant has duplicate records. Totals on this page may be split across records —
        verify before relying on them.
      </Alert>
    );
  }

  if (pairs.length === 0) return null;

  if (variant === "quiet") {
    return (
      <Stack spacing={0.5} data-testid="suspected-duplicate-quiet">
        {pairs.map((pair) => {
          const resolved = counterpartOf(pair, claimantId);
          if (!resolved) return null;
          return (
            <Typography key={pair.id} variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
              May be the same person as{" "}
              <Box component="span" sx={{ color: "text.primary", fontWeight: 500 }}>
                <ReferenceField
                  record={pair}
                  source={resolved.counterpartSource}
                  reference={ResourceName.CLAIMANTS}
                  link="show"
                >
                  <TextField source="claimant_name" />
                </ReferenceField>
              </Box>
              {canList ? (
                <>
                  {" · "}
                  <MuiLink
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => setReviewing(pair)}
                    sx={{ font: "inherit", verticalAlign: "baseline" }}
                    data-testid="suspected-duplicate-review"
                  >
                    Review
                  </MuiLink>
                </>
              ) : null}
            </Typography>
          );
        })}
        {reviewing ? (
          <DuplicateClaimantPairReviewDialog key={reviewing.id} pair={reviewing} onClose={() => setReviewing(null)} />
        ) : null}
      </Stack>
    );
  }

  return (
    <Alert severity="warning" sx={{ mb: 2, "& .MuiAlert-message": { fontSize: "0.8125rem", width: "100%" } }}>
      <AlertTitle sx={{ fontSize: "0.875rem", fontWeight: 600, mb: 0.5 }}>
        {pairs.length === 1
          ? "This claimant may be the same person as another record"
          : `This claimant may be the same person as ${pairs.length} other records`}
      </AlertTitle>

      <Typography variant="body2" sx={{ fontSize: "0.8125rem", mb: 1 }}>
        Spending is accumulating separately against the specific deductible. Review the pair before relying on this
        claimant&rsquo;s totals.
      </Typography>

      <Stack spacing={1.25}>
        {pairs.map((pair) => (
          <DuplicatePairEvidence key={pair.id} pair={pair} claimantId={claimantId} canList={canList} />
        ))}
      </Stack>
    </Alert>
  );
};

/**
 * One pair's deciding evidence, inline. The AC requires a user to decide without
 * opening two claimants and a policy, so every figure that decision needs is
 * here: what matched, what differs, both sides' spend, the combined total, and
 * the deductible it is measured against.
 */
const DuplicatePairEvidence = ({
  pair,
  claimantId,
  canList,
}: {
  pair: DuplicateClaimantPair;
  claimantId: string;
  canList: boolean;
}) => {
  const resolved = counterpartOf(pair, claimantId);

  // The pair does not touch the claimant being viewed, so which side is "this record"
  // is undecidable. Render nothing rather than a banner whose figures are the wrong way
  // round — see counterpartOf. Should be unreachable: the endpoint is scoped to this
  // claimant.
  if (!resolved) return null;

  const { counterpartSource, viewedPaidAmount, counterpartPaidAmount } = resolved;

  const differing = (pair.differing_fields ?? []).map((f) => DIFFERING_FIELD_LABELS[f] ?? f).join(", ");

  return (
    <Box sx={{ pl: 0.25 }}>
      <Stack direction="row" spacing={0.75} alignItems="baseline" flexWrap="wrap">
        <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
          Possible duplicate of
        </Typography>
        {/* ReferenceField resolves the counterpart's NAME from its id and links
            to its show page — the same mechanism ClaimantList and #6717's list
            use, so the name rendered here cannot drift from those. */}
        <Box component="span" sx={{ fontWeight: 600 }}>
          <ReferenceField record={pair} source={counterpartSource} reference={ResourceName.CLAIMANTS} link="show">
            <TextField source="claimant_name" />
          </ReferenceField>
        </Box>
      </Stack>

      {/* matched_on is NOT rendered. It arrives in three different casings depending on
          which name tier fired -- raw display name from one recorder call site, strict-
          normalized from another, core form from a third -- so it showed as
          `on "yvonne williams"` two lines under a properly-cased **Yvonne Williams**
          from the ReferenceField, and inconsistently between pairs (review !4646 thread
          27722; the tier drift itself is #6813).

          Title-casing it here would paper over that. It also carries little for the
          user: the counterpart's actual name is directly above, so the matched value
          adds a second rendering of the same information in worse shape. */}
      <Typography variant="body2" sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.25 }}>
        Found by {DETECTION_REASON_LABELS[pair.detection_reason] ?? pair.detection_reason}
        {differing ? ` · differs on ${differing}` : ""}
        {pair.was_re_raised ? " · previously dismissed, re-raised because the evidence changed" : ""}
      </Typography>

      <Typography variant="body2" sx={{ fontSize: "0.75rem", mt: 0.25, fontVariantNumeric: "tabular-nums" }}>
        This record {formatAmount(viewedPaidAmount)} · other record {formatAmount(counterpartPaidAmount)} ·{" "}
        <Box component="span" sx={{ fontWeight: 600 }}>
          combined {formatAmount(pair.combined_paid_amount)}
        </Box>
        {pair.specific_deductible ? ` of ${formatAmount(pair.specific_deductible)} deductible` : ""}
      </Typography>

      {/* Gated on list permission: a user who cannot open the review list should
          not be offered a link into it. The evidence above still renders — the
          warning is worth showing even to someone who cannot action it.

          FILTERED TO THIS CLIENT rather than landing on the partner-wide queue.
          AC 3 asks for a link to THE PAIR, and no per-pair route exists yet — the
          resource registers `list` only and the API has no GET /{pairId} (both open
          on !4630 thread 27547). `client_id` IS a supported list filter though, and
          #6717's aside already exposes it, so this narrows to a handful of rows
          instead of the whole queue at no backend cost (review !4646 thread 27723).
          It degrades into a real deep link once a Show view exists.

          The filter is JSON in a query param because that is react-admin's own
          list-filter encoding — the list reads it back through useListController
          rather than needing anything custom here. `status` must be in it: a URL
          filter REPLACES the list's filterDefaultValues, and the API returns every
          status when none is sent, so dismissed pairs would stay on the list. It is
          the pair's OWN status, not "open": the banner also shows confirmed pairs,
          and pinning "open" would land on a list without the pair. */}
      {canList && (
        <MuiLink
          component={RouterLink}
          to={`/${ResourceName.DUPLICATE_CLAIMANT_PAIRS}?filter=${encodeURIComponent(
            JSON.stringify({ client_id: pair.client_id, status: pair.status })
          )}`}
          sx={{ fontSize: "0.75rem", mt: 0.25, display: "inline-block" }}
        >
          Review in duplicate claimants
        </MuiLink>
      )}
    </Box>
  );
};
